import {
  extractStatusDisplayItem,
  formatTransportStatuses,
  sortStatusesByTimestamp,
  statusDisplayTimeMs,
  type EtlTransportResponse,
  type StatusDisplayItem,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import type { ResourceRequestQueueCategory } from '../../JurisdictionView/EventPane/ResourceRequest/adapters/types';
import { RESOURCE_REQUEST_ACTIVE_TIMEOUT_MS } from '../../JurisdictionView/EventPane/ResourceRequest/queueRetention';
import {
  STATUS_PAGE_STATES,
  STATUS_TYPES,
} from '../AirAmbulanceForm/constants';
import type { StatusPageState, StatusType } from '../types';

export interface ResolvedStatusEta {
  utcTime: string;
  isArrived: boolean;
}

/** Latest GMR label and badge tier for queue chips and call queue subtitles. */
export interface TransportStatusChip {
  display: string;
  statusType: string | undefined;
}

const CLEARED_ETA_STATUS_TYPES: ReadonlySet<string> = new Set([
  'complete',
  'cancelled',
  'pendingCancellation',
  'cancelPending',
  'cancelCompleted',
  'error',
]);

const ARRIVAL_ETA_STATUS_TYPES: ReadonlySet<string> = new Set([
  'arrivedToScene',
  'arrivedToDropoff',
]);

/**
 * Returns STATUS_TYPES entry only when `type` is an own property key, so user
 * supplied strings cannot read Object.prototype or pollute lookup results.
 *
 * @param type Raw status type string from ETL payloads
 * @returns Matching config or undefined when absent or not an own key of STATUS_TYPES
 */
export function getStatusTypeConfig(
  type: string | undefined,
): StatusType | undefined {
  if (typeof type !== 'string' || type === '') {
    return undefined;
  }

  if (!Object.hasOwn(STATUS_TYPES, type)) {
    return undefined;
  }

  return STATUS_TYPES[type];
}

function isCancelledStatusType(type: string | undefined): boolean {
  const config = getStatusTypeConfig(type);
  return config?.pageState.value === STATUS_PAGE_STATES.CANCELLED.value;
}

/**
 * Resolve ETA display from the latest status type, with a fallback search
 * across all statuses for the most recent utcTime.
 *
 * Terminal statuses clear ETA entirely. When the latest status has no ETA,
 * the search continues through older statuses so a prior ETA is not lost.
 *
 * @param latestStatus Latest formatted status row, or null or undefined when absent
 * @param formattedStatuses All formatted statuses, newest-first
 * @returns Resolved ETA with utcTime and isArrived flag, or null when ETA should not display
 */
function resolveStatusEtaDisplay(
  latestStatus: StatusDisplayItem | null | undefined,
  formattedStatuses: StatusDisplayItem[],
): ResolvedStatusEta | null {
  const type = latestStatus?.type;
  if (typeof type !== 'string' || type === '') {
    return null;
  }

  if (CLEARED_ETA_STATUS_TYPES.has(type)) {
    return null;
  }

  // Search all statuses, newest-first, looking for the first one with a utcTime ETA.
  // The latest status may arrive with an empty eta, even though a prior one had one.
  const utcTime = formattedStatuses.find(s => s.eta?.[0]?.utcTime)?.eta?.[0]
    ?.utcTime;
  if (!utcTime) {
    return null;
  }

  return { utcTime, isArrived: ARRIVAL_ETA_STATUS_TYPES.has(type) };
}

/**
 * Determine the status page state based on transport data and statuses.
 * Approximates the behavior of getStatusState in statusUtils.ts, adapted for
 * the ETL transport shape (is_test flag instead of launch_type).
 */
function getStatusPageState(
  transport: EtlTransportResponse | undefined,
  statuses: StatusDisplayItem[],
): StatusPageState {
  // No transport yet: On the status page, we may show it just after the request
  // has been made but ETL hasn't responded. We want to return _something_ here
  // to avoid flickering and show the user that something is happening.
  if (!transport) {
    return STATUS_PAGE_STATES.SUBMITTED;
  }

  // The midas-bvased behavior always shows this test message anytime the
  // transport is a test
  if (transport.is_test) {
    return STATUS_PAGE_STATES.TEST;
  }

  if (statuses.some(status => isCancelledStatusType(status.type))) {
    return STATUS_PAGE_STATES.CANCELLED;
  }

  // Statuses are already sorted newest-first; use the first with a known type key
  for (const s of statuses) {
    const config = getStatusTypeConfig(s.type);
    if (config) {
      return config.pageState;
    }
  }

  // Have statuses but none with a known type — default to ACCEPTED
  if (statuses.length > 0) {
    return STATUS_PAGE_STATES.ACCEPTED;
  }

  return STATUS_PAGE_STATES.SUBMITTED;
}

function isDisplayableStatusRow(status: StatusDisplayItem): boolean {
  return Boolean(status.display && getStatusTypeConfig(status.type));
}

/**
 * Find the newest status row with both a non-empty display and a recognized
 * type. GMR occasionally pushes follow-up rows that lack a display string or
 * use a type not in {@link STATUS_TYPES}; using those as the displayed row
 * would blank the banner back to the generic page-state message.
 */
function findLatestDisplayableStatus(
  formattedStatuses: StatusDisplayItem[],
): StatusDisplayItem | undefined {
  return formattedStatuses.find(isDisplayableStatusRow);
}

/** Latest displayable status row, plus older rows for the timeline accordion. */
export interface DisplayableStatusTimeline {
  latestDisplayableStatus: StatusDisplayItem | null;
  remainingStatuses: StatusDisplayItem[];
}

function resolveDisplayableStatusTimeline(
  formattedStatuses: StatusDisplayItem[],
): DisplayableStatusTimeline {
  const index = formattedStatuses.findIndex(isDisplayableStatusRow);
  const found = index !== -1;
  const latestDisplayableStatus = found
    ? (formattedStatuses[index] ?? null)
    : null;
  const remainingStatusesStart = found ? index + 1 : 1;
  const remainingStatuses = formattedStatuses.slice(remainingStatusesStart);

  return { latestDisplayableStatus, remainingStatuses };
}

/**
 * Newest status row with a recognized type. GMR can push follow-up rows with
 * unrecognized types; skipping them keeps a transient row from changing the
 * derived category. Statuses are expected newest-first.
 */
function findLatestRecognizedStatus(
  statuses: StatusDisplayItem[],
): StatusDisplayItem | undefined {
  return statuses.find(status => getStatusTypeConfig(status.type));
}

/**
 * True when the transport's latest recognized status is an error. Newest-wins:
 * a later non-error status moves the request back out of the Error section.
 */
function hasErrorStatus(statuses: StatusDisplayItem[]): boolean {
  return findLatestRecognizedStatus(statuses)?.type === 'error';
}

function resolveTransportStatusChip(
  transport: EtlTransportResponse,
): TransportStatusChip | undefined {
  const formattedStatuses = formatTransportStatuses(transport.statuses);
  const displayable = findLatestDisplayableStatus(formattedStatuses);
  const display = displayable?.display;
  if (!display) {
    return undefined;
  }
  const statusType = hasErrorStatus(formattedStatuses)
    ? 'error'
    : displayable.type;
  return { display, statusType };
}

/** GMR label for active-dispatch banners; undefined for terminal page states. */
function getActiveDispatchStatusDisplay(
  transport: EtlTransportResponse,
): string | undefined {
  const formattedStatuses = formatTransportStatuses(transport.statuses);
  if (
    getStatusPageState(transport, formattedStatuses).value !==
    STATUS_PAGE_STATES.ACCEPTED.value
  ) {
    return undefined;
  }
  return findLatestDisplayableStatus(formattedStatuses)?.display;
}

/**
 * Single source of truth for which queue section a resource request belongs to,
 * shared by the list grouping and the detail-pane move toggle.
 *
 * Precedence: an error status wins; then a manual "closed" override; then the
 * 6h-inactivity rule (a manual "active" override counts as fresh activity, so
 * reopening restarts the window); otherwise active. A manual close stays put
 * until reopened — cancelled/completed do not auto-close.
 */
function getResourceRequestQueueCategory(
  transport: EtlTransportResponse,
  statuses: StatusDisplayItem[],
  nowMs: number,
): ResourceRequestQueueCategory {
  if (hasErrorStatus(statuses)) {
    return 'error';
  }
  if (transport.queue_category_override === 'closed') {
    return 'closed';
  }
  const reopenedAtMs =
    transport.queue_category_override === 'active'
      ? parseTimestampMs(transport.queue_category_override_at)
      : 0;
  const lastActivityMs = getLatestTransportActivityMs(transport, statuses);
  const effectiveActivityMs = Math.max(lastActivityMs, reopenedAtMs);
  if (nowMs - effectiveActivityMs >= RESOURCE_REQUEST_ACTIVE_TIMEOUT_MS) {
    return 'closed';
  }
  return 'active';
}

/**
 * Latest activity timestamp for a transport, in epoch ms.
 * Falls back to the transport's `created_at` when no statuses exist or all
 * status timestamps are unparseable.
 */
function getLatestTransportActivityMs(
  transport: EtlTransportResponse,
  statuses: StatusDisplayItem[],
): number {
  for (const s of statuses) {
    const ms = statusDisplayTimeMs(s);
    if (ms > 0) {
      return ms;
    }
  }
  const createdMs = new Date(transport.created_at).getTime();
  return Number.isFinite(createdMs) ? createdMs : 0;
}

/**
 * Parse an ISO timestamp string to epoch ms, or 0 when absent or unparseable.
 */
function parseTimestampMs(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Map caller_id to latest transport status display string for call queue rows.
 * When several transports share a caller, keeps the status with the newest timestamp.
 */
function getAirAmbulanceStatusByCallerID(
  transports: EtlTransportResponse[] | undefined | null,
): Record<string, string> {
  const displayByCaller: Record<string, string> = {};
  const bestTimeMsByCaller: Record<string, number> = {};

  for (const transport of transports ?? []) {
    const callerId = transport.caller_id;
    if (!callerId) {
      continue;
    }

    const chip = resolveTransportStatusChip(transport);
    if (!chip) {
      continue;
    }

    const timeMs = getLatestTransportActivityMs(
      transport,
      formatTransportStatuses(transport.statuses),
    );
    const prevBest = bestTimeMsByCaller[callerId];
    if (prevBest === undefined || timeMs >= prevBest) {
      bestTimeMsByCaller[callerId] = timeMs;
      displayByCaller[callerId] = chip.display;
    }
  }

  return displayByCaller;
}

export {
  extractStatusDisplayItem,
  sortStatusesByTimestamp,
  resolveStatusEtaDisplay,
  formatTransportStatuses,
  findLatestDisplayableStatus,
  getActiveDispatchStatusDisplay,
  resolveDisplayableStatusTimeline,
  resolveTransportStatusChip,
  hasErrorStatus,
  getResourceRequestQueueCategory,
  getStatusPageState,
  getAirAmbulanceStatusByCallerID,
  getLatestTransportActivityMs,
};
